import { useTranslation } from 'react-i18next';
import { IMAGES } from '@/data/images';

export default function InfoSections() {
  const { t } = useTranslation();

  return (
    <>
      {/* Intro */}
      <section className="py-14 md:py-20 bg-background-100/60">
        <div className="mx-auto max-w-7xl px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">
              {t('home.intro.title')}
            </h2>
            <p className="mt-4 text-foreground-600 leading-relaxed">{t('home.intro.p1')}</p>
            <p className="mt-3 text-foreground-600 leading-relaxed">{t('home.intro.p2')}</p>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                t('home.intro.point1'),
                t('home.intro.point2'),
                t('home.intro.point3'),
                t('home.intro.point4'),
              ].map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm text-foreground-700">
                  <i className="ri-checkbox-circle-line text-primary-500 mt-0.5" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden">
            <img src={IMAGES.intro} alt="USFans Spreadsheet overview" className="w-full h-full object-cover object-top" />
          </div>
        </div>
      </section>

      {/* QC */}
      <section id="qc" className="py-14 md:py-20 bg-background-50">
        <div className="mx-auto max-w-7xl px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="order-2 lg:order-1 w-full h-64 md:h-80 rounded-lg overflow-hidden">
            <img src={IMAGES.qc} alt="USFans QC quality check" className="w-full h-full object-cover object-top" />
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">
              {t('home.qc.title')}
            </h2>
            <p className="mt-4 text-foreground-600 leading-relaxed">{t('home.qc.body')}</p>
            <ul className="mt-6 space-y-3">
              {[t('home.qc.item1'), t('home.qc.item2'), t('home.qc.item3'), t('home.qc.item4')].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-foreground-700">
                  <span className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center shrink-0">
                    <i className="ri-check-line" />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Review / Legit */}
      <section className="py-14 md:py-20 bg-background-100/60">
        <div className="mx-auto max-w-7xl px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">
              {t('home.review.title')}
            </h2>
            <p className="mt-4 text-foreground-600 leading-relaxed">{t('home.review.body')}</p>
            <div className="mt-6 rounded-lg bg-background-50 border border-background-200 p-5">
              <h3 className="font-heading font-semibold text-foreground-950 flex items-center gap-2">
                <i className="ri-shield-check-line text-primary-500" />
                {t('home.review.legitTitle')}
              </h3>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[t('home.review.legit1'), t('home.review.legit2'), t('home.review.legit3'), t('home.review.legit4')].map(
                  (item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-foreground-700">
                      <i className="ri-checkbox-circle-line text-primary-500 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  )
                )}
              </ul>
            </div>
          </div>
          <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden">
            <img src={IMAGES.shipping} alt="USFans shipping and warehouse" className="w-full h-full object-cover object-top" />
          </div>
        </div>
      </section>

      {/* Shipping & Warehouse */}
      <section id="shipping" className="py-14 md:py-20 bg-background-50">
        <div className="mx-auto max-w-7xl px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
            <div className="w-11 h-11 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
              <i className="ri-truck-line text-xl" />
            </div>
            <h3 className="mt-4 font-heading font-semibold text-xl text-foreground-950">
              {t('home.shipping.title')}
            </h3>
            <p className="mt-3 text-foreground-600 leading-relaxed">{t('home.shipping.body')}</p>
            <ul className="mt-4 space-y-2">
              {[t('home.shipping.item1'), t('home.shipping.item2'), t('home.shipping.item3')].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-foreground-700">
                  <i className="ri-arrow-right-line text-primary-500 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
            <div className="w-11 h-11 rounded-lg bg-accent-100 text-accent-600 flex items-center justify-center">
              <i className="ri-archive-line text-xl" />
            </div>
            <h3 className="mt-4 font-heading font-semibold text-xl text-foreground-950">
              {t('home.warehouse.title')}
            </h3>
            <p className="mt-3 text-foreground-600 leading-relaxed">{t('home.warehouse.body')}</p>
            <div className="mt-5 w-full h-44 rounded-lg overflow-hidden">
              <img src={IMAGES.warehouse} alt="USFans warehouse" className="w-full h-full object-cover object-top" />
            </div>
          </div>
        </div>
      </section>

      {/* Coupon & Safety */}
      <section className="py-14 md:py-20 bg-background-100/60">
        <div className="mx-auto max-w-7xl px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
            <div className="w-11 h-11 rounded-lg bg-secondary-100 text-secondary-600 flex items-center justify-center">
              <i className="ri-coupon-3-line text-xl" />
            </div>
            <h3 className="mt-4 font-heading font-semibold text-xl text-foreground-950">
              {t('home.coupon.title')}
            </h3>
            <p className="mt-3 text-foreground-600 leading-relaxed">{t('home.coupon.body')}</p>
          </div>

          <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
            <div className="w-11 h-11 rounded-lg bg-accent-100 text-accent-600 flex items-center justify-center">
              <i className="ri-shield-star-line text-xl" />
            </div>
            <h3 className="mt-4 font-heading font-semibold text-xl text-foreground-950">
              {t('home.safety.title')}
            </h3>
            <p className="mt-3 text-foreground-600 leading-relaxed">{t('home.safety.body')}</p>
            <ul className="mt-4 space-y-2">
              {[t('home.safety.item1'), t('home.safety.item2'), t('home.safety.item3'), t('home.safety.item4')].map(
                (item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-line text-accent-500 mt-0.5" />
                    <span>{item}</span>
                  </li>
                )
              )}
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}